import React, { useState, useEffect } from 'react';
import { creditNoteService } from '../../../api/apiService';
import { userService } from '../../../api/apiService';
import '../../../App.css';
import './TalentCreditNotesList.css';

/**
 * Credit notes for talents managed by the current user (agency / multiple talents).
 * Only shown when the user has multiple talent profiles. Read-only: list + PDF/XML download.
 */
function TalentCreditNotesList() {
  const [creditNotes, setCreditNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState({});
  const [talentCount, setTalentCount] = useState(0);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const profileRes = await userService.getProfile();
        const userData = profileRes?.data?.user || profileRes?.data;
        const talents = userData?.talents || [];
        setTalentCount(talents.length);

        const res = await creditNoteService.getCreditNotesForMyTalents({ limit: 200 });
        const data = res.data;
        setCreditNotes(data?.creditNotes || []);
      } catch (err) {
        console.error('Error loading credit notes:', err);
        setError(err.message || 'Failed to load credit notes');
        setCreditNotes([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const formatDate = (d) => {
    if (!d) return '—';
    const date = typeof d === 'string' ? new Date(d) : d;
    return isNaN(date.getTime()) ? String(d) : date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const formatDateTime = (d) => {
    if (!d) return '—';
    const date = typeof d === 'string' ? new Date(d) : d;
    if (isNaN(date.getTime())) return String(d);
    return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const formatTime = (d) => {
    if (!d) return '—';
    const date = typeof d === 'string' ? new Date(d) : d;
    if (isNaN(date.getTime())) return String(d);
    return date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  };

  const formatMoney = (n) => {
    if (n == null || n === '') return '—';
    const num = typeof n === 'number' ? n : parseFloat(n);
    return isNaN(num) ? String(n) : num.toFixed(2);
  };

  const displayAgency = (name) => {
    const s = name != null ? String(name).trim() : '';
    if (s.length <= 1) return '—';
    return s;
  };

  const handleDownloadPdf = async (cn) => {
    const key = `pdf-${cn.id}`;
    setDownloading(prev => ({ ...prev, [key]: true }));
    try {
      const res = await creditNoteService.getCreditNotePdf(cn.id);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Gutschrift_${cn.credit_note_number || cn.id}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('PDF download failed:', err);
    } finally {
      setDownloading(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleDownloadXml = async (cn) => {
    const key = `xml-${cn.id}`;
    setDownloading(prev => ({ ...prev, [key]: true }));
    try {
      const res = await creditNoteService.getCreditNoteXml(cn.id);
      const blob = new Blob([res.data], { type: 'application/xml; charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Gutschrift_${cn.credit_note_number || cn.id}.xml`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('XML download failed:', err);
    } finally {
      setDownloading(prev => ({ ...prev, [key]: false }));
    }
  };

  if (loading) {
    return (
      <div className="content-wrapper page">
        <div className="content-wrapper">
          <h1 className="h1">Credit notes</h1>
          <p className="talent-cn-loading">Loading…</p>
        </div>
      </div>
    );
  }

  if (talentCount <= 1) {
    return (
      <div className="content-wrapper page">
        <div className="content-wrapper">
          <h1 className="h1">Credit notes</h1>
          <p className="talent-cn-info">
            This view is for agency accounts that manage multiple talents. You have one talent profile linked. Credit notes for your bookings are available from your main Projects view and from the admin team.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="content-wrapper page">
      <div className="content-wrapper">
        <h1 className="h1">Credit notes</h1>
        <p className="talent-cn-description">
          Credit notes issued for the talents you manage. Download PDF or XRechnung XML (German e-invoice) as needed.
        </p>
        {error && (
          <div className="talent-cn-error">{error}</div>
        )}
        <div className="talent-cn-table-wrap">
          <table className="talent-cn-table">
            <thead>
              <tr>
                <th>Number</th>
                <th>Type</th>
                <th>Status</th>
                <th>Date</th>
                <th>Created</th>
                <th>Created by</th>
                <th>Talent</th>
                <th>Agency</th>
                <th>AP %</th>
                <th>Net</th>
                <th>VAT</th>
                <th className="credit-notes-col-gross">Gross</th>
                <th>Export</th>
              </tr>
            </thead>
            <tbody>
              {creditNotes.length === 0 ? (
                <tr>
                  <td colSpan={13} className="talent-cn-empty">
                    No credit notes yet for the talents you manage.
                  </td>
                </tr>
              ) : (
                creditNotes.map((cn) => (
                  <tr key={cn.id}>
                    <td>{cn.credit_note_number || cn.id}</td>
                    <td>{cn.invoice_type || '—'}</td>
                    <td>{cn.status || '—'}</td>
                    <td>
                      {cn.booking_dates ? (
                        <div className="credit-notes-booking-dates">
                          {cn.booking_dates.split(/\s*,\s*|\n/).filter(Boolean).map((date, i) => (
                            <div key={i}>{date.trim()}</div>
                          ))}
                        </div>
                      ) : formatDate(cn.invoice_date)}
                    </td>
                    <td>
                      {cn.created_at ? (
                        <div className="credit-notes-booking-dates">
                          <div>{formatDate(cn.created_at)}</div>
                          <div>{formatTime(cn.created_at)}</div>
                        </div>
                      ) : '—'}
                    </td>
                    <td>{cn.created_by || '—'}</td>
                    <td>{cn.talent_name || '—'}</td>
                    <td>{displayAgency(cn.agency_name)}</td>
                    <td>{cn.agency_percentage != null ? `${Number(cn.agency_percentage)}%` : '—'}</td>
                    <td>{formatMoney(cn.net_amount)}</td>
                    <td>{formatMoney(cn.vat_amount)}</td>
                    <td className="credit-notes-col-gross">{formatMoney(cn.gross_amount)}</td>
                    <td>
                      <button
                        type="button"
                        onClick={() => handleDownloadPdf(cn)}
                        disabled={downloading[`pdf-${cn.id}`]}
                        className="button talent-cn-export-btn"
                      >
                        {downloading[`pdf-${cn.id}`] ? '…' : 'PDF'}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDownloadXml(cn)}
                        disabled={downloading[`xml-${cn.id}`]}
                        className="button talent-cn-export-btn"
                      >
                        {downloading[`xml-${cn.id}`] ? '…' : 'XML'}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default TalentCreditNotesList;
